import { navigate, onRoute } from "./router.js";
import { GameLoop } from "../game/loop.js";
import { Diagnostics } from "../game/diagnostics.js";
import { store } from "./state/store.js";
import { requestMic, buildGraph, compileEngine, EventStream, DEFAULT_DSP_CONFIG, saveDspConfig, loadModel, } from "@beatbox/voice-core";
import { Canvas2DRenderer } from "../render/canvas2d.js";
import { WebGLVisualizer } from "../render/webgl-viz.js";
import { Haptics } from "../render/haptics.js";
import { Sonifier } from "../render/sonify.js";
import { generateCourse } from "../game/courses/index.js";
import { labelFor } from "../theme/naming.js";
import { iconFor } from "../theme/icons.js";
import { loadCustomPhonemes, loadHiddenPresets, loadPresetOverrides, } from "../storage/phonemes.js";
import { getActiveLibrary, listLibraries, setActiveLibraryId, } from "../storage/libraries.js";
import "../ui/ac-button.js";
import "../ui/ac-course-card.js";
import "../ui/ac-game-settings.js";
import "../ui/ac-hud.js";
import "../ui/ac-diagnostics.js";
import "../ui/ac-pop-panel.js";
import "../ui/ac-gain-panel.js";
import "../ui/ac-sustain-panel.js";
import "../ui/ac-level-designer.js";
import "../ui/ac-results.js";
export class AgilityShell extends HTMLElement {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "loop", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "diagnostics", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "viz", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "sonifier", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "vizStream", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "vizRaf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "popPanel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "gainPanel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "sustainPanel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "graphDispose", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "unsub", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "renderGen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "percussiveIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "libraryLoaded", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "hiddenPresetIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "customPhonemeIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "trainedPhonemeIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "activeLibraryPhonemeIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "activeLibraryIsDefault", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "phonemeMeta", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
    }
    connectedCallback() {
        this.unsub = onRoute((route) => this.render(route));
    }
    disconnectedCallback() {
        this.loop?.dispose();
        this.loop = null;
        this.sonifier?.dispose();
        this.sonifier = null;
        this.closePopPanel();
        this.closeGainPanel();
        this.closeSustainPanel();
        this.diagnostics?.dispose();
        this.diagnostics = null;
        this.disposeVisualizer();
        this.graphDispose?.();
        this.graphDispose = null;
        this.unsub?.();
        this.unsub = null;
    }
    closePopPanel() {
        if (this.popPanel) {
            this.popPanel.remove();
            this.popPanel = null;
        }
    }
    closeGainPanel() {
        if (this.gainPanel) {
            this.gainPanel.remove();
            this.gainPanel = null;
        }
    }
    closeSustainPanel() {
        if (this.sustainPanel) {
            this.sustainPanel.remove();
            this.sustainPanel = null;
        }
    }
    disposeVisualizer() {
        cancelAnimationFrame(this.vizRaf);
        this.vizRaf = 0;
        this.viz?.dispose();
        this.viz = null;
        this.vizStream?.dispose();
        this.vizStream = null;
    }
    isPercussiveId(id) {
        const presetLanes = ["hiss", "airy", "pop", "hum"];
        if (this.percussiveIds.has(id))
            return true;
        if (!this.libraryLoaded && presetLanes.includes(id))
            return id === "pop";
        return false;
    }
    impulseLabels() {
        const settings = store.gameSettings.get();
        const out = [];
        for (const id of this.percussiveIds) {
            if (this.hiddenPresetIds.has(id))
                continue;
            if (settings.enabled[id] === false)
                continue;
            out.push(id);
        }
        return out;
    }
    classifierConfigFromSettings() {
        const s = store.gameSettings.get();
        return {
            C: s.classifierC,
            gamma: s.classifierGamma,
            rejectConfidence: s.classifierRejectConfidence,
            testFraction: s.classifierTestFraction,
            impulseCBoost: s.classifierImpulseCBoost,
        };
    }
    async pitchInvariantLabels() {
        const invariant = new Set();
        const presetLanes = ["hiss", "airy", "pop", "hum"];
        let overrides = {};
        try {
            overrides = await loadPresetOverrides();
        }
        catch { }
        this.percussiveIds.clear();
        this.hiddenPresetIds.clear();
        this.activeLibraryPhonemeIds.clear();
        this.trainedPhonemeIds.clear();
        try {
            const activeLib = await getActiveLibrary();
            this.activeLibraryIsDefault = activeLib.id === "default";
            for (const p of activeLib.phonemes) {
                this.activeLibraryPhonemeIds.add(p.id);
                if (p.trained)
                    this.trainedPhonemeIds.add(p.id);
                if ((p.pitchInvariant ?? true) !== false)
                    invariant.add(p.id);
                if (p.percussive)
                    this.percussiveIds.add(p.id);
                this.phonemeMeta.set(p.id, {
                    description: p.description,
                    pronunciation: p.pronunciation,
                });
            }
        }
        catch (err) {
            console.warn("[AgilityShell] failed to load active library", err);
            this.activeLibraryIsDefault = true;
        }
        try {
            const hidden = await loadHiddenPresets();
            for (const id of hidden)
                this.hiddenPresetIds.add(id);
        }
        catch { }
        for (const id of presetLanes) {
            const perc = overrides[id]?.percussive ?? id === "pop";
            if (perc)
                this.percussiveIds.add(id);
        }
        for (const id of presetLanes) {
            if ((overrides[id]?.pitchInvariant ?? true) !== false) {
                invariant.add(id);
            }
        }
        try {
            const custom = await loadCustomPhonemes();
            this.customPhonemeIds.clear();
            for (const p of custom) {
                this.customPhonemeIds.add(p.id);
                if ((p.pitchInvariant ?? true) !== false)
                    invariant.add(p.id);
                if (p.percussive)
                    this.percussiveIds.add(p.id);
            }
        }
        catch { }
        this.libraryLoaded = true;
        return invariant;
    }
    async hasTrainedPhonemes() {
        try {
            const activeLib = await getActiveLibrary();
            return activeLib.phonemes.some((p) => p.trained);
        }
        catch (err) {
            console.warn("[AgilityShell] failed to check trained phonemes", err);
            return false;
        }
    }
    async renderQuickCal() {
        console.info("[AgilityShell] renderQuickCal");
        const gen = this.renderGen;
        this.innerHTML = `
<div style="position:relative;height:100%;overflow:hidden;background:#080a16">
   <div style="position:absolute;inset:0;z-index:0;background:
       linear-gradient(180deg,rgba(8,10,22,.35),rgba(8,10,22,.75)),
       url('/images/quickcal.png') center/cover no-repeat"></div>
   <div id="qcstage" style="position:relative;z-index:2;max-width:640px;margin:0 auto;padding:32px;color:#fff">
     <h1>Setup</h1>
     <div id="qcprogress" style="height:8px;border-radius:4px;
         background:rgba(255,255,255,.12);overflow:hidden;margin:14px 0">
       <div id="qcbar" style="height:100%;width:0%;
           background:linear-gradient(90deg,#22d3ee,#4ade80);
           transition:width .3s ease"></div>
     </div>
     <p id="qcinfo" style="opacity:.8">Setting up microphone…</p>
   </div>
   <div style="position:absolute;bottom:16px;left:16px;right:16px;display:flex;gap:10px;z-index:2">
     <ac-button id="qcback" variant="ghost">Back</ac-button>
   </div>
</div>
`;
        this.querySelector("#qcback")?.addEventListener("click", () => {
            navigate("home");
        });
        const mic = await requestMic();
        if (mic.denied || !mic.stream) {
            const info = this.querySelector("#qcinfo");
            if (info) {
                info.textContent = `Microphone unavailable (${mic.error ?? "denied"}).`;
            }
            return;
        }
        let graph;
        try {
            const compiled = await compileEngine();
            graph = await buildGraph(mic.stream, compiled?.module ?? null, compiled?.factory ?? null, undefined, compiled?.glueUrl ?? null, compiled?.wasmUrl ?? null, compiled?.bytes ?? null);
            await graph.resume();
            store.usingWasm.set(graph.engine.usingWasm);
        }
        catch (err) {
            console.error("[AgilityShell] quickcal graph failed", err);
            for (const t of mic.stream.getTracks())
                t.stop();
            return;
        }
        if (gen !== this.renderGen) {
            graph.dispose();
            return;
        }
        this.graphDispose = graph.dispose;
        this.diagnostics = new Diagnostics({
            engine: graph.engine,
            ctx: graph.ctx,
            onSnapshot: (snap) => {
                this.gainPanel?.update(snap);
                this.popPanel?.update(snap);
                this.sustainPanel?.update(snap);
            },
        });
        this.diagnostics.start();
        // Load the phoneme labels for the active library FIRST so the
        // calibration plan reflects exactly the sounds that will be played
        // (respecting hidden presets, custom phonemes, and per-sound enabled
        // settings) rather than a hard-coded hiss/airy/pop/hum set.
        const pitchInvariant = await this.pitchInvariantLabels();
        if (gen !== this.renderGen)
            return;
        this.diagnostics?.setPitchInvariantLabels(pitchInvariant);
        const theme = "child";
        const setInfo = (txt) => {
            const info = this.querySelector("#qcinfo");
            if (info)
                info.textContent = txt;
        };
        // A reusable prompt card: shows an instructive message and an OK button
        // that must be pressed before the corresponding calibration step begins.
        const prompt = (title, body, okLabel = "OK") => new Promise((resolve) => {
            if (gen !== this.renderGen) {
                resolve();
                return;
            }
            const existing = this.querySelector("#qcprompt");
            existing?.remove();
            const stage = this.querySelector("#qcstage");
            if (!stage) {
                resolve();
                return;
            }
            const card = document.createElement("div");
            card.id = "qcprompt";
            card.style.cssText =
                "margin-top:18px;padding:16px 18px;border-radius:14px;" +
                    "background:rgba(18,22,42,.72);border:1px solid #2a2f4a";
            card.innerHTML = `
                 <div style="font-size:17px;font-weight:800;margin-bottom:8px">
                   ${title}</div>
                 <div style="font-size:14px;line-height:1.5;opacity:.9;
                     margin-bottom:14px">${body}</div>
                 <ac-button id="qcpromptok" variant="primary">${okLabel}</ac-button>`;
            stage.appendChild(card);
            card.querySelector("#qcpromptok")?.addEventListener("click", () => {
                card.remove();
                resolve();
            });
        });
        // Present the preset-selection step first, then compute the plan from
        // whatever library is active afterward.
        await this.chooseLibraryStep(gen);
        if (gen !== this.renderGen)
            return;
        // Offer to clear all previous calibration and start fresh.
        await this.freshStartStep(gen);
        if (gen !== this.renderGen)
            return;
        // Reload labels for the (possibly newly-selected) active library.
        const pitchInvariant2 = await this.pitchInvariantLabels();
        if (gen !== this.renderGen)
            return;
        this.diagnostics?.setPitchInvariantLabels(pitchInvariant2);
        const plan = this.calibrationPlan();
        // Steps: gain (1) + silence (1) + percussive + sustained.
        const total = 2 + plan.percussive.length + plan.sustained.length;
        const setProgress = (completed) => {
            const bar = this.querySelector("#qcbar");
            if (bar) {
                const pct = Math.round((completed / Math.max(1, total)) * 100);
                bar.style.width = `${Math.max(0, Math.min(100, pct))}%`;
            }
        };
        const stepInfo = (n, txt) => {
            setInfo(`Step ${n} of ${total}: ${txt}`);
            setProgress(n - 1);
        };
        const finish = () => {
            if (gen !== this.renderGen)
                return;
            setProgress(total);
            setInfo("Calibration complete! Your voice is ready to play.");
            const stage = this.querySelector("#qcstage");
            if (!stage || stage.querySelector("#qcdone"))
                return;
            const done = document.createElement("div");
            done.id = "qcdone";
            done.style.cssText =
                "display:flex;gap:10px;flex-wrap:wrap;margin-top:20px";
            done.innerHTML = `
                <ac-button id="qcplay" variant="primary">▶ Play now</ac-button>
                <ac-button id="qchome" variant="ghost">Done</ac-button>`;
            stage.appendChild(done);
            done
                .querySelector("#qcplay")
                ?.addEventListener("click", () => navigate("play"));
            done
                .querySelector("#qchome")
                ?.addEventListener("click", () => navigate("home"));
        };
        const runSustain = (idx) => {
            if (gen !== this.renderGen)
                return;
            if (idx >= plan.sustained.length) {
                finish();
                return;
            }
            const id = plan.sustained[idx];
            const n = 3 + plan.percussive.length + idx;
            stepInfo(n, `hold the "${labelFor(id, theme)}" sound steadily.`);
            void prompt(`Hold the "${labelFor(id, theme)}" sound ${iconFor(id, theme)}`, `When you press Start, hold the "${labelFor(id, theme)}" sound ` +
                `steadily while the ball is inside the highlighted window, and ` +
                `stay quiet when it's outside. If this sound is hard to make, ` +
                `you can skip it.`, "Start").then(() => {
                if (gen !== this.renderGen)
                    return;
                const sp = document.createElement("ac-sustain-panel");
                sp.setAttribute("cluster", id);
                sp.setAttribute("glyph", iconFor(id, theme));
                sp.setAttribute("label", labelFor(id, theme));
                sp.addEventListener("sustain-hold-start", (e) => {
                    const cid = e.detail
                        ?.clusterId;
                    if (cid)
                        this.diagnostics?.startCalibrateHold(cid);
                });
                sp.addEventListener("sustain-hold-end", () => {
                    this.diagnostics?.endCalibrateHold();
                });
                sp.addEventListener("sustain-done", () => {
                    this.closeSustainPanel();
                    runSustain(idx + 1);
                });
                sp.addEventListener("sustain-skip", () => {
                    this.diagnostics?.endCalibrateHold();
                    this.closeSustainPanel();
                    runSustain(idx + 1);
                });
                sp.addEventListener("sustain-cancel", () => {
                    this.closeSustainPanel();
                    navigate("home");
                });
                this.sustainPanel = sp;
                this.appendChild(sp);
            });
        };
        const runPercussive = (idx) => {
            if (gen !== this.renderGen)
                return;
            if (idx >= plan.percussive.length) {
                runSustain(0);
                return;
            }
            const id = plan.percussive[idx];
            const n = 3 + idx;
            stepInfo(n, `tap out your "${labelFor(id, theme)}" (pop/plosive).`);
            const meta = this.phonemeMeta.get(id);
            const extra = (meta?.description ? `${meta.description} ` : "") +
                (meta?.pronunciation ? `How: ${meta.pronunciation}` : "");
            void prompt(`Tap the "${labelFor(id, theme)}" sound ${iconFor(id, theme)}`, `When you press Start, pop this sound each time the ball is ` +
                `inside the highlighted window, and stay quiet outside it. ` +
                (extra ? `<br><br>${extra}` : "") +
                `<br><br>If this sound is hard to make, you can skip it.`, "Start").then(() => {
                if (gen !== this.renderGen)
                    return;
                this.diagnostics?.startPopPanel(id);
                const pp = document.createElement("ac-pop-panel");
                pp.setAttribute("cluster", id);
                if (meta?.description) {
                    pp.setAttribute("description", meta.description);
                }
                if (meta?.pronunciation) {
                    pp.setAttribute("pronunciation", meta.pronunciation);
                }
                pp.addEventListener("poppanel-event", (ev) => {
                    const d = ev.detail;
                    if (d)
                        this.diagnostics?.popPanelCommit(d.outcome, d.frames);
                });
                pp.addEventListener("poppanel-done", () => {
                    void this.diagnostics?.endPopPanel();
                    this.closePopPanel();
                    runPercussive(idx + 1);
                });
                pp.addEventListener("poppanel-skip", () => {
                    this.diagnostics?.cancelPopPanel();
                    this.closePopPanel();
                    runPercussive(idx + 1);
                });
                pp.addEventListener("poppanel-cancel", () => {
                    this.diagnostics?.cancelPopPanel();
                    this.closePopPanel();
                    navigate("home");
                });
                this.popPanel = pp;
                this.appendChild(pp);
            });
        };
        const runGain = () => {
            if (gen !== this.renderGen)
                return;
            stepInfo(1, "set input levels with the bouncing ball.");
            void prompt("Set your microphone levels", `When you press Start, speak, hum, or pop steadily while the ` +
                `ball is inside the highlighted window, and stay quiet while ` +
                `it's outside. We'll measure your mic automatically and move on ` +
                `once the levels are dialed in.`, "Start").then(() => {
                if (gen !== this.renderGen)
                    return;
                this.diagnostics?.startGainPanel();
                const gp = document.createElement("ac-gain-panel");
                gp.setSource(graph.ctx, graph.source);
                gp.addEventListener("gainpanel-applied", () => {
                    this.diagnostics?.pushDspConfig();
                });
                gp.addEventListener("gainpanel-done", () => {
                    this.diagnostics?.endGainPanel();
                    this.closeGainPanel();
                    runSilence();
                });
                gp.addEventListener("gainpanel-cancel", () => {
                    this.diagnostics?.cancelGainPanel();
                    this.closeGainPanel();
                    navigate("home");
                });
                this.gainPanel = gp;
                this.appendChild(gp);
            });
        };
        const runSilence = () => {
            if (gen !== this.renderGen)
                return;
            setInfo("Recording a few seconds of silence…");
            void prompt("Record silence 🤫", `When you press Start, please <b>stay completely quiet</b> for ` +
                `a few seconds. This teaches the app what your room sounds like ` +
                `when you're not making any sound, so it can tell silence apart ` +
                `from your voice.`, "Start").then(() => {
                if (gen !== this.renderGen)
                    return;
                setInfo("Listening to the quiet… hold still and stay silent.");
                void this.diagnostics
                    ?.recordSilence(4000)
                    .then((n) => {
                    console.info(`[AgilityShell] dedicated silence step captured ` +
                        `${n} frame(s)`);
                    runPercussive(0);
                })
                    .catch((err) => {
                    console.warn("[AgilityShell] silence capture failed", err);
                    runPercussive(0);
                });
            });
        };
        runGain();
    }
    /**
     * Derive the quick-calibration plan from the active library and the
     * per-sound enabled settings, splitting sounds into percussive (Pop Panel)
     * and sustained (hold) groups. This keeps Setup in lock-step with whatever
     * will actually be played, including custom/library phonemes.
     */
    /**
     * First quick-setup step: let the user pick a phoneme preset library (or
     * keep the currently-active one). Resolves once a choice is made so the
     * remaining calibration steps use the selected library's sounds.
     */
    async chooseLibraryStep(gen) {
        if (gen !== this.renderGen)
            return;
        let libraries;
        try {
            libraries = await listLibraries();
        }
        catch (err) {
            console.warn("[AgilityShell] failed to list libraries", err);
            return;
        }
        if (gen !== this.renderGen)
            return;
        const activeId = this.activeLibraryPhonemeIds.size >= 0
            ? await getActiveLibrary()
                .then((l) => l.id)
                .catch(() => "default")
            : "default";
        const stage = this.querySelector("#qcstage");
        if (!stage)
            return;
        await new Promise((resolve) => {
            const card = document.createElement("div");
            card.id = "qcprompt";
            card.style.cssText =
                "margin-top:18px;padding:16px 18px;border-radius:14px;" +
                    "background:rgba(18,22,42,.72);border:1px solid #2a2f4a";
            const options = libraries
                .map((l) => `<option value="${l.id}" ` +
                `${l.id === activeId ? "selected" : ""}>` +
                `${l.name}${l.builtin ? " (built-in)" : ""}` +
                `${l.experimental ? " — experimental" : ""}</option>`)
                .join("");
            card.innerHTML = `
             <div style="font-size:17px;font-weight:800;margin-bottom:8px">
               Choose a sound set</div>
             <div style="font-size:14px;line-height:1.5;opacity:.9;
                 margin-bottom:14px">
               Pick a phoneme preset to calibrate, or continue with your
               current configuration. Switching a preset changes which sounds
               you'll train and play.</div>
             <select id="qclibsel"
                 style="pointer-events:auto;padding:8px 10px;border-radius:8px;
                 border:1px solid #2a2f4a;background:#12162a;color:#fff;
                 font-size:14px;min-width:220px;margin-bottom:14px">
               ${options}</select>
             <div style="display:flex;gap:10px">
               <ac-button id="qclibok" variant="primary">Continue</ac-button>
             </div>`;
            stage.appendChild(card);
            card.querySelector("#qclibok")?.addEventListener("click", () => {
                const sel = card.querySelector("#qclibsel");
                const chosen = sel?.value ?? activeId;
                card.remove();
                void (async () => {
                    if (chosen && chosen !== activeId) {
                        try {
                            await setActiveLibraryId(chosen);
                            console.info(`[AgilityShell] setup selected library ` + `'${chosen}'`);
                        }
                        catch (err) {
                            console.warn("[AgilityShell] failed to set library", err);
                        }
                    }
                    resolve();
                })();
            });
        });
    }
    /**
     * Offer the user the chance to wipe all previous calibration (training data
     * and audio parameters) and start fresh. Shows a confirmation dialog when
     * "Start fresh" is chosen.
     */
    async freshStartStep(gen) {
        if (gen !== this.renderGen)
            return;
        let hasPrevious = false;
        try {
            const model = await loadModel();
            hasPrevious =
                (await this.hasTrainedPhonemes()) ||
                    (!!model && model.labels.length >= 2);
        }
        catch {
            hasPrevious = false;
        }
        if (!hasPrevious)
            return;
        if (gen !== this.renderGen)
            return;
        const stage = this.querySelector("#qcstage");
        if (!stage)
            return;
        await new Promise((resolve) => {
            const card = document.createElement("div");
            card.id = "qcprompt";
            card.style.cssText =
                "margin-top:18px;padding:16px 18px;border-radius:14px;" +
                    "background:rgba(18,22,42,.72);border:1px solid #2a2f4a";
            card.innerHTML = `
             <div style="font-size:17px;font-weight:800;margin-bottom:8px">
               Start fresh?</div>
             <div style="font-size:14px;line-height:1.5;opacity:.9;
                 margin-bottom:14px">
               You already have calibration data. You can keep it and only
               recalibrate the sounds you want, or clear <b>all</b> previous
               training data and audio parameters to start completely fresh.</div>
             <div style="display:flex;gap:10px;flex-wrap:wrap">
               <ac-button id="qckeep" variant="ghost">Keep &amp; continue</ac-button>
               <ac-button id="qcfresh" variant="primary">Start fresh…</ac-button>
             </div>`;
            stage.appendChild(card);
            card.querySelector("#qckeep")?.addEventListener("click", () => {
                card.remove();
                resolve();
            });
            card.querySelector("#qcfresh")?.addEventListener("click", () => {
                // Confirmation dialog.
                card.innerHTML = `
                 <div style="font-size:17px;font-weight:800;margin-bottom:8px;
                     color:#f87171">Clear everything?</div>
                 <div style="font-size:14px;line-height:1.5;opacity:.9;
                     margin-bottom:14px">
                   This will permanently delete all trained sounds and reset your
                   microphone/audio settings to defaults. This cannot be undone.
                 </div>
                 <div style="display:flex;gap:10px;flex-wrap:wrap">
                   <ac-button id="qcfreshcancel" variant="ghost">Cancel</ac-button>
                   <ac-button id="qcfreshconfirm" variant="primary">
                     Yes, clear everything</ac-button>
                 </div>`;
                card.querySelector("#qcfreshcancel")?.addEventListener("click", () => {
                    card.remove();
                    resolve();
                });
                card.querySelector("#qcfreshconfirm")?.addEventListener("click", () => {
                    void (async () => {
                        try {
                            await this.diagnostics?.clearEverything();
                            await saveDspConfig({ ...DEFAULT_DSP_CONFIG });
                            console.info("[AgilityShell] fresh start: cleared all " +
                                "calibration and reset DSP config");
                        }
                        catch (err) {
                            console.warn("[AgilityShell] fresh start failed", err);
                        }
                        card.remove();
                        resolve();
                    })();
                });
            });
        });
    }
    calibrationPlan() {
        const settings = store.gameSettings.get();
        const ids = this.activeLibraryIsDefault
            ? [
                ...["hiss", "airy", "pop", "hum"].filter((id) => !this.hiddenPresetIds.has(id)),
                ...this.customPhonemeIds,
            ]
            : [...this.activeLibraryPhonemeIds];
        const percussive = [];
        const sustained = [];
        for (const id of ids) {
            if (settings.enabled[id] === false)
                continue;
            if (this.isPercussiveId(id))
                percussive.push(id);
            else
                sustained.push(id);
        }
        console.info(`[AgilityShell] calibration plan ` +
            `percussive=[${percussive.join(",")}] ` +
            `sustained=[${sustained.join(",")}]`);
        return { percussive, sustained };
    }
    render(route) {
        this.loop?.dispose();
        this.loop = null;
        this.sonifier?.dispose();
        this.sonifier = null;
        this.closePopPanel();
        this.closeGainPanel();
        this.closeSustainPanel();
        this.diagnostics?.dispose();
        this.diagnostics = null;
        this.disposeVisualizer();
        this.graphDispose?.();
        this.graphDispose = null;
        this.renderGen += 1;
        const guard = (p, label) => {
            void p.catch((err) => {
                console.error(`[AgilityShell] render '${label}' failed`, err);
            });
        };
        switch (route) {
            case "play":
                guard(this.renderPlay(), "play");
                break;
            case "quickcal":
                guard(this.renderQuickCal(), "quickcal");
                break;
            case "designer":
                this.renderDesigner();
                break;
            case "visualizer":
                guard(this.renderVisualizer(), "visualizer");
                break;
            case "diagnostics":
                guard(this.renderDiagnostics(), "diagnostics");
                break;
            case "home":
            default:
                guard(this.renderHome(), "home");
                break;
        }
    }
    async renderHome() {
        const hasTrained = await this.hasTrainedPhonemes();
        this.innerHTML = `
<style>
  @keyframes vpBreathe {
    0%,100% { transform: scale(1); opacity:.85 }
    50%     { transform: scale(1.06); opacity:1 }
  }
  @keyframes vpRing {
    0%   { transform: scale(.85); opacity:.15 }
    70%  { opacity:.5 }
    100% { transform: scale(1.6); opacity:0 }
  }
  @keyframes vpSheen {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes vpRise {
    from { opacity:0; transform: translateY(16px); }
    to   { opacity:1; transform: translateY(0); }
  }
  @keyframes vpPulse {
    0%,100% { box-shadow:0 0 0 0 rgba(34,211,238,.55); }
    50%     { box-shadow:0 0 40px 6px rgba(34,211,238,.35); }
  }
  .vp-home { position:relative; height:100%; overflow:hidden;
    background:#080a16; }
  .vp-bg { position:absolute; inset:0; z-index:0; background-size:cover;
    background-position:center; background-repeat:no-repeat;
    animation:vpBreathe 12s ease-in-out infinite; }
  .vp-bg-tint { position:absolute; inset:0; z-index:0; pointer-events:none;
    background:
      radial-gradient(1200px 700px at 20% 20%, rgba(8,10,22,0) 30%, rgba(8,10,22,.55) 100%),
      linear-gradient(180deg, rgba(8,10,22,.35) 0%, rgba(8,10,22,.15) 40%, rgba(8,10,22,.7) 100%); }
  .vp-sigil { position:absolute; z-index:1; left:50%; top:42%;
    transform:translate(-50%,-50%); width:min(46vw,420px);
    aspect-ratio:1; pointer-events:none; }
  .vp-sigil .core { position:absolute; inset:34%; border-radius:50%;
    background:radial-gradient(circle,#67e8f9 0%,#22d3ee 55%,rgba(34,211,238,0) 75%);
    filter:blur(6px); animation:vpBreathe 6s ease-in-out infinite; }
  .vp-sigil .ring { position:absolute; inset:20%; border-radius:50%;
    border:2px solid rgba(34,211,238,.5); animation:vpRing 5s ease-out infinite; }
  .vp-sigil .ring:nth-child(2) { animation-delay:1.6s; }
  .vp-sigil .ring:nth-child(3) { animation-delay:3.2s; }
  .vp-title {
    font-size:clamp(46px, 11vw, 128px); font-weight:900; line-height:.95;
    letter-spacing:-.03em; margin:0;
    background:linear-gradient(100deg,#e8ecff 0%,#22d3ee 45%,#4ade80 70%,#e8ecff 100%);
    background-size:200% auto; -webkit-background-clip:text; background-clip:text;
    color:transparent; animation:vpSheen 10s linear infinite;
    text-shadow:0 8px 60px rgba(34,211,238,.35); }
  .vp-sub { font-size:clamp(15px,2.4vw,20px); opacity:.85; margin:14px 0 0;
    max-width:560px; line-height:1.5; }
  .vp-tag { display:inline-flex; align-items:center; gap:8px;
    padding:6px 14px; border-radius:999px; font-size:13px; font-weight:700;
    letter-spacing:.12em; text-transform:uppercase; color:#a7e8ef;
    background:rgba(34,211,238,.12); border:1px solid rgba(34,211,238,.35);
    backdrop-filter:blur(6px); }
  .vp-cta { display:flex; gap:14px; margin-top:34px; flex-wrap:wrap; }
  .vp-btn { position:relative; cursor:pointer; user-select:none;
    padding:16px 28px; border-radius:16px; font-weight:800; font-size:17px;
    color:#fff; border:1px solid rgba(255,255,255,.14);
    background:rgba(20,24,44,.65); backdrop-filter:blur(10px);
    transition:transform .12s ease, box-shadow .2s ease, background .2s ease; }
  .vp-btn:hover { transform:translateY(-3px); background:rgba(30,36,64,.8); }
  .vp-btn--play { background:linear-gradient(120deg,#22d3ee,#4ade80);
    border:none; animation:vpPulse 3.2s ease-in-out infinite; }
  .vp-btn--play:hover { box-shadow:0 12px 40px rgba(34,211,238,.55); }
  .vp-btn--ghost { background:rgba(20,24,44,.4); }
  .vp-btn--disabled { opacity:.4; cursor:not-allowed; filter:grayscale(.5);
    animation:none !important; }
  .vp-btn--disabled:hover { transform:none; box-shadow:none; }
  .vp-stack { position:relative; z-index:2; height:100%; display:flex;
    flex-direction:column; justify-content:center;
    padding:6vh clamp(24px,6vw,88px); box-sizing:border-box; max-width:1100px; }
  .vp-rise { animation:vpRise .7s cubic-bezier(.2,.8,.2,1) both; }
  .vp-foot { position:absolute; bottom:20px; left:0; right:0; text-align:center;
    z-index:2; font-size:12px; letter-spacing:.14em; text-transform:uppercase;
    opacity:.4; }
  @media (prefers-reduced-motion: reduce) {
    .vp-bg, .vp-sigil .core, .vp-sigil .ring, .vp-title, .vp-btn--play,
    .vp-rise { animation:none !important; }
  }
</style>
<div class="vp-home">

  <div class="vp-bg" style="background-image:url('/images/hero.png')"></div>
  <div class="vp-bg-tint"></div>
  <div class="vp-sigil">
    <div class="ring"></div>
    <div class="ring"></div>
    <div class="ring"></div>
    <div class="core"></div>
  </div>

  <div class="vp-stack">
    <span class="vp-tag vp-rise" style="animation-delay:.05s;align-self:flex-start">
      ● Vocal-gesture agility
    </span>
    <h1 class="vp-title vp-rise" style="animation-delay:.12s">Vocal<br>Parkour</h1>
    <p class="vp-sub vp-rise" style="animation-delay:.22s">
      Leap from sound to sound. Hiss, pop, hum and breathe your way down the
      rail — in time, in tune, and entirely on your own voice.
    </p>
    ${hasTrained
            ? ""
            : `
    <p class="vp-sub vp-rise" style="animation-delay:.26s;color:#fbbf24;
        font-size:14px;max-width:560px">
      ⚠ No sounds are trained yet. Run <b>Setup</b> to calibrate your voice
      before playing or designing a stage.
    </p>`}
    <div class="vp-cta vp-rise" style="animation-delay:.32s">
      <div class="vp-btn vp-btn--play${hasTrained ? "" : " vp-btn--disabled"}"
          id="play"${hasTrained ? "" : ' aria-disabled="true"'}>▶ Play</div>
      <div class="vp-btn vp-btn--ghost" id="quickcal">⚡ Setup</div>
      <div class="vp-btn vp-btn--ghost${hasTrained ? "" : " vp-btn--disabled"}"
          id="designer"${hasTrained ? "" : ' aria-disabled="true"'}>🎛 Level Designer</div>
         <div class="vp-btn vp-btn--ghost" id="visualizer">🌈 Visualizer</div>
      <div class="vp-btn vp-btn--ghost" id="diagnostics">⚙ Settings</div>
    </div>
  </div>
  <div class="vp-foot">Runs entirely on-device · your voice never leaves your machine</div>
</div>
`;
        this.querySelector("#quickcal")?.addEventListener("click", () => navigate("quickcal"));
        this.querySelector("#play")?.addEventListener("click", () => {
            if (hasTrained)
                navigate("play");
            else
                console.warn("[AgilityShell] Play blocked: no trained sounds");
        });
        this.querySelector("#designer")?.addEventListener("click", () => {
            if (hasTrained)
                navigate("designer");
            else
                console.warn("[AgilityShell] Level Designer blocked: no trained sounds");
        });
        this.querySelector("#visualizer")?.addEventListener("click", () => navigate("visualizer"));
        this.querySelector("#diagnostics")?.addEventListener("click", () => navigate("diagnostics"));
    }
    async renderPlay() {
        console.info("[AgilityShell] renderPlay");
        if (!store.activeCourse.get()) {
            this.renderGameSetup();
            return;
        }
        await this.startPlay();
    }
    renderDesigner() {
        console.info("[AgilityShell] renderDesigner");
        this.innerHTML = `
<div style="position:relative;height:100%;overflow:auto;background:#080a16">
   <div style="position:fixed;inset:0;z-index:0;pointer-events:none;background:
       linear-gradient(180deg,rgba(8,10,22,.55),rgba(8,10,22,.82)),
       url('/images/designer.png') center/cover no-repeat"></div>
   <div style="padding:32px 24px 96px">
     <ac-level-designer></ac-level-designer>
   </div>
   <div style="position:fixed;bottom:16px;left:16px;right:16px;display:flex;gap:10px;z-index:2">
     <ac-button id="back" variant="ghost">Back</ac-button>
   </div>
</div>
`;
        this.querySelector("#back")?.addEventListener("click", () => navigate("home"));
        this.querySelector("ac-level-designer")?.addEventListener("designer-play", () => {
            console.info("[AgilityShell] designer-play → starting stage");
            store.activeCourse.set("designed");
            navigate("play");
        });
    }
    async renderVisualizer() {
        console.info("[AgilityShell] renderVisualizer");
        const gen = this.renderGen;
        this.innerHTML = `
<div style="position:relative;height:100%;overflow:hidden;background:#04060f">
   <canvas data-viz-canvas
       style="position:absolute;inset:0;width:100%;height:100%;display:block"></canvas>
   <div style="position:absolute;top:16px;left:0;right:0;text-align:center;
       pointer-events:none;z-index:2">
     <div style="display:inline-block;padding:6px 16px;border-radius:999px;
         background:rgba(10,12,24,.55);border:1px solid rgba(34,211,238,.4);
         font-size:13px;letter-spacing:.14em;text-transform:uppercase;
         color:#a7e8ef;backdrop-filter:blur(6px)">
       🌈 Wacky Signal Visualizer
     </div>
   </div>
   <div data-viz-hint style="position:absolute;bottom:70px;left:0;right:0;
       text-align:center;pointer-events:none;z-index:2;font-size:13px;
       opacity:.75;color:#fff">
     Make some noise! Hiss, hum, pop and sing to drive the lights.
   </div>
   <div style="position:absolute;bottom:16px;left:16px;right:16px;display:flex;
       gap:10px;z-index:3">
     <ac-button id="vizback" variant="ghost">Back</ac-button>
   </div>
</div>
`;
        this.querySelector("#vizback")?.addEventListener("click", () => navigate("home"));
        const canvas = this.querySelector("[data-viz-canvas]");
        if (!canvas) {
            console.error("[AgilityShell] visualizer canvas missing");
            return;
        }
        const mic = await requestMic();
        if (gen !== this.renderGen) {
            for (const t of mic.stream?.getTracks() ?? [])
                t.stop();
            return;
        }
        if (mic.denied || !mic.stream) {
            const hint = this.querySelector("[data-viz-hint]");
            if (hint) {
                hint.textContent = `Microphone unavailable (${mic.error ?? "denied"}).`;
            }
            // Still run the visualizer with no input so it's not a blank screen.
            this.viz = new WebGLVisualizer(canvas);
            this.viz.start();
            return;
        }
        let graph;
        try {
            const compiled = await compileEngine();
            graph = await buildGraph(mic.stream, compiled?.module ?? null, compiled?.factory ?? null, undefined, compiled?.glueUrl ?? null, compiled?.wasmUrl ?? null, compiled?.bytes ?? null);
            await graph.resume();
            store.usingWasm.set(graph.engine.usingWasm);
        }
        catch (err) {
            console.error("[AgilityShell] visualizer graph failed", err);
            for (const t of mic.stream.getTracks())
                t.stop();
            // Still run a (silent) visualizer so the screen isn't blank.
            if (gen === this.renderGen) {
                this.viz = new WebGLVisualizer(canvas);
                this.viz.start();
            }
            return;
        }
        if (gen !== this.renderGen) {
            graph.dispose();
            return;
        }
        this.graphDispose = graph.dispose;
        this.viz = new WebGLVisualizer(canvas);
        this.viz.start();
        this.vizStream = new EventStream(graph.engine);
        // Direct URL entry (e.g. pasting #/visualizer) has no user gesture,
        // so the AudioContext may start suspended. Resume it on the first
        // pointer/keyboard interaction anywhere in the visualizer view.
        const resumeOnGesture = () => {
            void graph.resume().catch(() => { });
        };
        // Try an immediate resume too (works when navigated via a click).
        void graph.resume().catch(() => { });
        this.addEventListener("pointerdown", resumeOnGesture, { once: true });
        window.addEventListener("keydown", resumeOnGesture, { once: true });
        const pump = () => {
            if (gen !== this.renderGen)
                return;
            const events = this.vizStream?.drain() ?? [];
            for (const ev of events)
                this.viz?.feed(ev.features);
            this.vizRaf = requestAnimationFrame(pump);
        };
        this.vizRaf = requestAnimationFrame(pump);
    }
    renderGameSetup() {
        this.innerHTML = `
<div style="padding:32px;max-width:640px;margin:0 auto">
    <h1>Game Setup</h1>
    <p style="opacity:.8">Choose your sounds and tuning, then begin.</p>
    <ac-game-settings style="display:block;margin:16px 0"></ac-game-settings>
    <div style="display:flex;gap:10px;margin-top:8px">
     <ac-button id="back" variant="ghost">Back</ac-button>
      <ac-button id="begin" variant="primary">Begin Game</ac-button>
   </div>
</div>
`;
        this.querySelector("#back")?.addEventListener("click", () => navigate("home"));
        this.querySelector("#begin")?.addEventListener("click", () => {
            store.activeCourse.set("custom");
            void this.startPlay();
        });
    }
    async startPlay() {
        const gen = this.renderGen;
        this.innerHTML = `
<div style="position:relative;height:100%">
  <canvas style="width:100%;height:100%;display:block"></canvas>
  <ac-hud></ac-hud>
  <div style="position:absolute;bottom:16px;left:16px;right:16px;display:flex;gap:10px">
    <ac-button id="quit" variant="ghost">Quit</ac-button>
  </div>
</div>
`;
        this.querySelector("#quit")?.addEventListener("click", () => {
            console.info("[AgilityShell] quit clicked");
            this.loop?.dispose();
            this.loop = null;
            const fromDesigner = store.activeCourse.get() === "designed";
            store.activeCourse.set(null);
            navigate(fromDesigner ? "designer" : "home");
        });
        const canvas = this.querySelector("canvas");
        if (!canvas) {
            console.error("[AgilityShell] canvas element missing; aborting play");
            return;
        }
        const mic = await requestMic();
        if (gen !== this.renderGen) {
            for (const t of mic.stream?.getTracks() ?? [])
                t.stop();
            return;
        }
        if (mic.denied || !mic.stream) {
            console.warn(`[AgilityShell] mic unavailable denied=${mic.denied} ` +
                `error=${mic.error ?? "none"}`);
            return;
        }
        console.info("[AgilityShell] mic granted");
        let graph;
        try {
            const compiled = await compileEngine();
            console.info(`[AgilityShell] engine compiled wasm=${!!compiled}`);
            graph = await buildGraph(mic.stream, compiled?.module ?? null, compiled?.factory ?? null, undefined, compiled?.glueUrl ?? null, compiled?.wasmUrl ?? null, compiled?.bytes ?? null);
            await graph.resume();
            store.usingWasm.set(graph.engine.usingWasm);
        }
        catch (err) {
            console.error("[AgilityShell] failed to build audio graph", err);
            for (const t of mic.stream.getTracks())
                t.stop();
            return;
        }
        if (gen !== this.renderGen) {
            graph.dispose();
            return;
        }
        this.graphDispose = graph.dispose;
        const renderer = new Canvas2DRenderer(canvas);
        const haptics = new Haptics(store.a11y.get().haptics);
        const sonifier = new Sonifier(graph.ctx);
        this.sonifier = sonifier;
        const pitchInvariant = await this.pitchInvariantLabels();
        if (gen !== this.renderGen) {
            graph.dispose();
            return;
        }
        const settings = store.gameSettings.get();
        const knownIds = this.activeLibraryIsDefault
            ? new Set([
                ...["hiss", "airy", "pop", "hum"].filter((id) => !this.hiddenPresetIds.has(id)),
                ...this.customPhonemeIds,
            ])
            : new Set(this.activeLibraryPhonemeIds);
        for (const id of [...knownIds]) {
            if (!this.trainedPhonemeIds.has(id))
                knownIds.delete(id);
        }
        const enabled = Object.keys(settings.enabled).filter((k) => settings.enabled[k] && knownIds.has(k));
        for (const id of knownIds) {
            if (!enabled.includes(id) && settings.enabled[id] !== false) {
                enabled.push(id);
            }
        }
        const lanes = enabled.length > 0
            ? enabled
            : this.activeLibraryIsDefault
                ? [...knownIds]
                : [...knownIds];
        if (store.activeCourse.get() === "designed") {
            const designedStage = store.designedStage.get();
            for (const n of designedStage?.notes ?? []) {
                if (!lanes.includes(n.clusterId) && knownIds.has(n.clusterId)) {
                    lanes.push(n.clusterId);
                }
            }
        }
        store.activeLanes.set(lanes);
        const laneOf = (id) => {
            const idx = lanes.indexOf(id);
            if (idx < 0) {
                console.warn(`[AgilityShell] laneOf unknown cluster '${id}'`);
            }
            const i = Math.max(0, idx);
            return (i + 1) * 90;
        };
        const colorOf = (id) => {
            const i = Math.max(0, lanes.indexOf(id));
            return lanes.length > 0 ? (i * 360) / lanes.length : 0;
        };
        const theme = "child";
        const glyphOf = (id) => iconFor(id, theme);
        const labelOf = (id) => labelFor(id, theme);
        renderer.setGlyphProvider(glyphOf);
        const percussiveOf = (id) => this.isPercussiveId(id);
        renderer.setPercussiveProvider(percussiveOf);
        console.info(`[AgilityShell] key (theme=${theme}): ` +
            lanes.map((id) => `${glyphOf(id)}=${labelOf(id)}`).join("  |  "));
        const BASE_TEMPO_BPM = 90;
        const designed = store.activeCourse.get() === "designed"
            ? store.designedStage.get()
            : null;
        let scaledCourse;
        if (designed && designed.notes.length > 0) {
            const speed = Math.max(0.2, Math.min(2, settings.speed || 1));
            scaledCourse = {
                id: "designed",
                title: designed.title,
                blurb: "A designed stage.",
                sequence: [...new Set(designed.notes.map((n) => n.clusterId))],
                tempoBpm: Math.round(designed.tempoBpm * speed),
                windowMs: Math.round(designed.windowMs / speed),
                infinite: false,
                plannedNotes: designed.notes.map((n) => ({
                    clusterId: n.clusterId,
                    beat: n.beat,
                    sustainBeats: n.sustainBeats,
                    pitchHz: n.pitchHz ?? null,
                })),
            };
            console.info(`[AgilityShell] playing DESIGNED stage '${designed.title}' ` +
                `notes=${designed.notes.length} tempo=${scaledCourse.tempoBpm}bpm`);
        }
        else {
            const effCourse = generateCourse(enabled, settings.length, BASE_TEMPO_BPM, settings.windowMs);
            const speed = Math.max(0.2, Math.min(2, settings.speed || 1));
            scaledCourse = {
                ...effCourse,
                tempoBpm: Math.round(effCourse.tempoBpm * speed),
                windowMs: Math.round(effCourse.windowMs / speed),
                infinite: settings.infinite,
            };
        }
        console.info(`[AgilityShell] course '${scaledCourse.id}' ` +
            `tempo=${scaledCourse.tempoBpm}bpm ` +
            `window=${scaledCourse.windowMs}ms ` +
            `enabled=[${enabled.join(",")}]`);
        this.loop = new GameLoop({
            engine: graph.engine,
            renderer,
            sonifier,
            haptics,
            ctx: graph.ctx,
            laneOf,
            colorOf,
            percussiveOf,
            perfectMs: settings.perfectMs,
            basePoints: settings.basePoints,
            comboCap: settings.comboCap,
            spawnIntervalBeats: settings.spawnIntervalBeats,
            sustainChance: settings.sustainChance,
            breakBeats: settings.breakBeats,
            sustainWeight1: settings.sustainWeight1,
            sustainWeight2: settings.sustainWeight2,
            sustainWeight4: settings.sustainWeight4,
            sustainWeight8: settings.sustainWeight8,
        });
        store.timed.set(true);
        this.loop.setPitchInvariantLabels(pitchInvariant);
        this.loop.setImpulseLabels(this.impulseLabels());
        this.loop.setEnabledClusters(store.activeLanes.get());
        const wasDesigned = store.activeCourse.get() === "designed";
        void this.loop.start(scaledCourse, true, () => {
            const result = this.loop?.getLastResult();
            if (result) {
                this.showResults(result, wasDesigned);
            }
            else {
                store.activeCourse.set(null);
                navigate(wasDesigned ? "designer" : "home");
            }
        });
    }
    showResults(result, wasDesigned) {
        this.loop?.dispose();
        this.loop = null;
        this.sonifier?.dispose();
        this.sonifier = null;
        this.graphDispose?.();
        this.graphDispose = null;
        const panel = document.createElement("ac-results");
        panel.data = result;
        panel.addEventListener("results-again", () => {
            panel.remove();
            void this.startPlay();
        });
        panel.addEventListener("results-quit", () => {
            panel.remove();
            store.activeCourse.set(null);
            navigate(wasDesigned ? "designer" : "home");
        });
        this.appendChild(panel);
    }
    async renderDiagnostics() {
        console.info("[AgilityShell] renderDiagnostics");
        const gen = this.renderGen;
        this.innerHTML = `
<div style="position:relative;height:100%;background:#080a16">
  <div style="position:fixed;inset:0;z-index:0;pointer-events:none;background:
      linear-gradient(180deg,rgba(8,10,22,.6),rgba(8,10,22,.85)),
      url('/images/diagnostics.png') center/cover no-repeat"></div>
  <ac-diagnostics></ac-diagnostics>
  <div style="position:absolute;bottom:16px;left:16px;right:16px;display:flex;gap:10px;z-index:2">
    <ac-button id="back" variant="ghost">Back</ac-button>
  </div>
</div>
`;
        this.querySelector("#back")?.addEventListener("click", () => {
            console.info("[AgilityShell] diagnostics back clicked");
            navigate("home");
        });
        this.querySelector("ac-diagnostics")?.addEventListener("open-visualizer", () => {
            console.info("[AgilityShell] opening visualizer from settings");
            navigate("visualizer");
        });
        const panel = this.querySelector("ac-diagnostics");
        if (!panel) {
            console.error("[AgilityShell] diagnostics panel missing");
            return;
        }
        const mic = await requestMic();
        if (mic.denied || !mic.stream) {
            console.warn(`[AgilityShell] mic unavailable denied=${mic.denied} ` +
                `error=${mic.error ?? "none"}`);
            panel.innerHTML = `
              <div style="max-width:720px;margin:0 auto;color:#fff;padding:24px">
                 <h1>Settings</h1>
                <p style="opacity:.8">
                  Microphone unavailable (${mic.error ?? "denied"}).
                   Grant mic access and reopen Settings.
                </p>
              </div>`;
            return;
        }
        console.info("[AgilityShell] mic granted (diagnostics)");
        let graph;
        try {
            const compiled = await compileEngine();
            graph = await buildGraph(mic.stream, compiled?.module ?? null, compiled?.factory ?? null, undefined, compiled?.glueUrl ?? null, compiled?.wasmUrl ?? null, compiled?.bytes ?? null);
            await graph.resume();
            store.usingWasm.set(graph.engine.usingWasm);
        }
        catch (err) {
            console.error("[AgilityShell] failed to build audio graph", err);
            for (const t of mic.stream.getTracks())
                t.stop();
            return;
        }
        if (gen !== this.renderGen) {
            graph.dispose();
            return;
        }
        this.graphDispose = graph.dispose;
        this.diagnostics = new Diagnostics({
            engine: graph.engine,
            ctx: graph.ctx,
            onSnapshot: (snap) => {
                panel.update(snap);
                this.popPanel?.update(snap);
                this.gainPanel?.update(snap);
            },
        });
        void this.pitchInvariantLabels().then((set) => this.diagnostics?.setPitchInvariantLabels(set));
        void this.pitchInvariantLabels().then(() => {
            this.diagnostics?.setImpulseLabels(this.impulseLabels());
            this.diagnostics?.configureClassifier(this.classifierConfigFromSettings());
        });
        panel.addEventListener("calibrate-start", (e) => {
            const id = e.detail?.clusterId;
            if (id) {
                console.info(`[AgilityShell] calibrate hold start '${id}'`);
                this.diagnostics?.startCalibrateHold(id);
            }
        });
        panel.addEventListener("calibrate-end", (e) => {
            const id = e.detail?.clusterId;
            if (id) {
                console.info(`[AgilityShell] calibrate hold end '${id}'`);
                this.diagnostics?.endCalibrateHold();
            }
        });
        panel.addEventListener("poppanel-open", (e) => {
            const id = e.detail?.clusterId;
            if (!id)
                return;
            if (this.popPanel)
                return;
            console.info(`[AgilityShell] pop-panel open '${id}'`);
            this.diagnostics?.startPopPanel(id);
            const pp = document.createElement("ac-pop-panel");
            pp.setAttribute("cluster", id);
            pp.addEventListener("poppanel-event", (ev) => {
                const d = ev.detail;
                if (d)
                    this.diagnostics?.popPanelCommit(d.outcome, d.frames);
            });
            pp.addEventListener("poppanel-done", () => {
                console.info("[AgilityShell] pop-panel done");
                void this.diagnostics?.endPopPanel();
                this.closePopPanel();
            });
            pp.addEventListener("poppanel-cancel", () => {
                console.info("[AgilityShell] pop-panel cancel");
                this.diagnostics?.cancelPopPanel();
                this.closePopPanel();
            });
            this.popPanel = pp;
            this.appendChild(pp);
        });
        panel.addEventListener("gainpanel-open", () => {
            if (this.gainPanel)
                return;
            console.info("[AgilityShell] gain-panel open");
            this.diagnostics?.startGainPanel();
            const gp = document.createElement("ac-gain-panel");
            gp.setSource(graph.ctx, graph.source);
            gp.addEventListener("gainpanel-applied", () => {
                console.info("[AgilityShell] gain-panel applied recommendation");
                this.diagnostics?.pushDspConfig();
                panel.refreshDspControls?.();
            });
            gp.addEventListener("gainpanel-done", () => {
                console.info("[AgilityShell] gain-panel done");
                this.diagnostics?.endGainPanel();
                this.closeGainPanel();
            });
            gp.addEventListener("gainpanel-cancel", () => {
                console.info("[AgilityShell] gain-panel cancel");
                this.diagnostics?.cancelGainPanel();
                this.closeGainPanel();
            });
            this.gainPanel = gp;
            this.appendChild(gp);
        });
        panel.addEventListener("train-clear-all", () => {
            console.info("[AgilityShell] clear ALL training data");
            this.diagnostics?.clearAllTrainingData();
        });
        panel.addEventListener("train-clear-label", (e) => {
            const id = e.detail?.label;
            if (id) {
                console.info(`[AgilityShell] clear training data '${id}'`);
                this.diagnostics?.clearTrainingLabel(id);
            }
        });
        panel.addEventListener("train-retrain", () => {
            console.info("[AgilityShell] explicit retrain");
            void this.diagnostics?.retrainNow();
        });
        panel.addEventListener("train-autoretrain", (e) => {
            const on = e.detail?.enabled;
            this.diagnostics?.setAutoRetrain(!!on);
        });
        panel.addEventListener("dsp-frame-hop", (e) => {
            const ms = e.detail?.frameHopMs;
            if (typeof ms === "number" && Number.isFinite(ms)) {
                console.info(`[AgilityShell] analysis packet duration ${ms}ms`);
                this.diagnostics?.setFrameHopMs(ms);
            }
            this.diagnostics?.pushDspConfig();
        });
        panel.addEventListener("dsp-config-changed", () => {
            this.diagnostics?.pushDspConfig();
        });
        panel.addEventListener("classifier-config-changed", (e) => {
            const patch = e.detail;
            if (patch) {
                console.info("[AgilityShell] classifier config changed", patch);
                this.diagnostics?.configureClassifier(patch);
            }
        });
        panel.addEventListener("library-changed", (e) => {
            const id = e.detail?.libraryId;
            console.info(`[AgilityShell] active library changed to '${id}' — ` +
                `re-applying labels and retraining classifier`);
            void this.pitchInvariantLabels().then((set) => {
                this.diagnostics?.setPitchInvariantLabels(set);
                this.diagnostics?.setImpulseLabels(this.impulseLabels());
                void this.diagnostics?.retrainNow();
            });
        });
        this.diagnostics.start();
    }
}
customElements.define("agility-shell", AgilityShell);
